import { useState } from 'react';
import { Alert, Tabs } from "antd";
import { useTranslation } from 'react-i18next';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@shared/ui';
import { ApiError } from '@shared/api';
import { applyApiFieldErrors } from '@shared/lib/forms/apply-field-errors';
import { COUNTRIES } from '@shared/lib/countries';
import { ROUTES } from '@shared/config/routes';
import { ALLOWED_CONTENT_TYPES } from '@entities/media';
import { MediaPicker } from '@features/media-picker';
import {
  useCreateProductMutation,
  useUpdateProductMutation,
  useSubmitProductMutation,
  useArchiveProductMutation,
  canSubmitProduct,
  canArchiveProduct,
  type ProductView,
} from '@entities/product';
import { useProfileQuery, isVendorVerified } from '@entities/manufacturer';
import {
  productFormSchema,
  emptyProductForm,
  productToForm,
  formToRequest,
  type ProductFormValues,
} from '../model/schema';
import { CategorySelect } from './category-select';
import { CurrencySelect } from '@shared/ui';

interface ProductFormProps {
  product?: ProductView;
}

export function ProductForm({ product }: ProductFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: profile } = useProfileQuery();
  const verified = isVendorVerified(profile?.kycStatus);

  const createMutation = useCreateProductMutation();
  const updateMutation = useUpdateProductMutation(product?.id ?? '');
  const submitMutation = useSubmitProductMutation();
  const archiveMutation = useArchiveProductMutation();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [nameTab, setNameTab] = useState<'ru' | 'en' | 'uz'>('ru');
  const [descTab, setDescTab] = useState<'ru' | 'en' | 'uz'>('ru');

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: product ? productToForm(product) : emptyProductForm(),
    mode: 'onBlur',
  });

  const localeLabel = (loc: 'ru' | 'en' | 'uz') => t(`common.locales.${loc}`);
  const renderTabLabel = (loc: 'ru' | 'en' | 'uz', hasError: boolean) => (
    <span style={{ color: hasError ? 'var(--ant-color-error)' : undefined }}>
      {localeLabel(loc)}
      {hasError ? <span aria-hidden> •</span> : null}
    </span>
  );
  const firstInvalid = (
    errs: Partial<Record<'ru' | 'en' | 'uz', unknown>> | undefined,
  ): 'ru' | 'en' | 'uz' | null => {
    if (!errs) return null;
    if (errs.ru) return 'ru';
    if (errs.en) return 'en';
    if (errs.uz) return 'uz';
    return null;
  };

  const isEco = form.watch('eco');
  const isStock = form.watch('stock');
  const photos = form.watch('photos');
  const status = product?.status;
  const isEditable = !product || (status !== 'PENDING_MODERATION' && status !== 'ARCHIVED');

  const onSubmit = (values: ProductFormValues) => {
    setSubmitError(null);
    const body = formToRequest(values);
    const mutation = product ? updateMutation : createMutation;
    mutation.mutate(body, {
      onSuccess: (saved) => {
        // On create — go straight into the saved item's edit page.
        // On update — return to the list (cache is patched in-place so no
        // extra GET is needed).
        if (!product) {
          navigate(ROUTES.productEdit(saved.id), { replace: true });
        } else {
          navigate(ROUTES.products);
        }
      },
      onError: (err) => {
        if (!applyApiFieldErrors(err, form.setError)) {
          setSubmitError(err instanceof ApiError ? err.message : 'Save failed');
        }
      },
    });
  };

  const handleSubmitForModeration = () => {
    if (!product) return;
    setSubmitError(null);
    submitMutation.mutate(product.id, {
      onError: (err) =>
        setSubmitError(err instanceof ApiError ? err.message : 'Submission failed'),
    });
  };

  const handleArchive = () => {
    if (!product) return;
    if (!window.confirm(t('products.archiveConfirm'))) return;
    archiveMutation.mutate(product.id, {
      onSuccess: () => navigate(ROUTES.products),
      onError: (err) =>
        setSubmitError(err instanceof ApiError ? err.message : 'Archive failed'),
    });
  };

  const submitDisabledReason = (() => {
    if (!product) return t('products.saveDisabled.saveFirst');
    if (!verified) return t('products.saveDisabled.kycRequired');
    if (photos.length === 0) return t('products.saveDisabled.noPhotos');
    if (!canSubmitProduct(product.status)) return null;
    return null;
  })();

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, (errors) => {
          const badName = firstInvalid(errors.name as never);
          if (badName) setNameTab(badName);
          const badDesc = firstInvalid(errors.description as never);
          if (badDesc) setDescTab(badDesc);
        })}
        noValidate
        className="flex flex-col gap-6"
      >
      {product && status === 'PENDING_MODERATION' ? (
        <Alert type="warning" showIcon message={t('products.banners.pendingModeration')} />
      ) : null}
      {product && status === 'REJECTED' && product.rejectionReason ? (
        <Alert type="error" showIcon message={t('products.banners.rejected')} description={product.rejectionReason} />
      ) : null}

      <Card>
        <CardContent className="grid gap-5 pt-6 md:grid-cols-2">
          <FormField
            label={t('products.fields.category')}
            required
            error={form.formState.errors.categoryId?.message}
          >
            {(p) => (
              <Controller
                control={form.control}
                name="categoryId"
                render={({ field }) => (
                  <CategorySelect
                    id={p.id}
                    invalid={p.invalid}
                    aria-describedby={p['aria-describedby']}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </FormField>

          <FormField label={t('products.fields.hsCode')} hint={t('products.fields.hsCodeHint')} error={form.formState.errors.hsCode?.message}>
            {(p) => <Input {...p} {...form.register('hsCode')} disabled={!isEditable} />}
          </FormField>

          <FormField
            label={t('products.fields.name')}
            required
            hint={t('products.fields.nameI18nHint')}
            error={(() => {
              const e = form.formState.errors.name;
              const bad = firstInvalid(e as never);
              if (!bad || !e?.[bad]?.message) return undefined;
              return t('products.fields.i18nLocaleError', {
                locale: localeLabel(bad),
                message: e[bad]?.message,
              });
            })()}
            className="md:col-span-2"
          >
            {(p) => (
              <Tabs
                size="small"
                activeKey={nameTab}
                onChange={(k) => setNameTab(k as 'ru' | 'en' | 'uz')}
                aria-describedby={p['aria-describedby']}
                items={(['ru', 'en', 'uz'] as const).map((loc) => ({
                  key: loc,
                  label: renderTabLabel(loc, !!form.formState.errors.name?.[loc]),
                  children: (
                    <Input
                      id={loc === 'ru' ? p.id : undefined}
                      {...form.register(`name.${loc}` as const)}
                      invalid={!!form.formState.errors.name?.[loc]}
                      disabled={!isEditable}
                    />
                  ),
                }))}
              />
            )}
          </FormField>

          <FormField
            label={t('products.fields.description')}
            hint={t('products.fields.descriptionI18nHint')}
            error={(() => {
              const e = form.formState.errors.description;
              const bad = firstInvalid(e as never);
              if (!bad || !e?.[bad]?.message) return undefined;
              return t('products.fields.i18nLocaleError', {
                locale: localeLabel(bad),
                message: e[bad]?.message,
              });
            })()}
            className="md:col-span-2"
          >
            {(p) => (
              <Tabs
                size="small"
                activeKey={descTab}
                onChange={(k) => setDescTab(k as 'ru' | 'en' | 'uz')}
                aria-describedby={p['aria-describedby']}
                items={(['ru', 'en', 'uz'] as const).map((loc) => ({
                  key: loc,
                  label: renderTabLabel(loc, !!form.formState.errors.description?.[loc]),
                  children: (
                    <Textarea
                      id={loc === 'ru' ? p.id : undefined}
                      rows={6}
                      {...form.register(`description.${loc}` as const)}
                      disabled={!isEditable}
                    />
                  ),
                }))}
              />
            )}
          </FormField>

          <FormField label={t('products.fields.moq')} required error={form.formState.errors.moq?.message}>
            {(p) => (
              <Input
                {...p}
                type="number"
                min={1}
                {...form.register('moq', { valueAsNumber: true })}
                disabled={!isEditable}
              />
            )}
          </FormField>

          <FormField label={t('products.fields.originCountry')} error={form.formState.errors.originCountry?.message}>
            {(p) => (
              <Controller
                control={form.control}
                name="originCountry"
                render={({ field }) => (
                  <Select
                    value={field.value ?? undefined}
                    onValueChange={(v) => field.onChange(v || null)}
                  >
                    <SelectTrigger id={p.id} invalid={p.invalid}>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.nameEn} ({c.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            )}
          </FormField>

          <FormField label={t('products.fields.price')} required error={form.formState.errors.priceAmount?.message}>
            {(p) => (
              <div className="flex gap-2">
                <Input
                  {...p}
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  className="flex-1"
                  {...form.register('priceAmount')}
                  disabled={!isEditable}
                />
                <Controller
                  control={form.control}
                  name="priceCurrency"
                  render={({ field }) => (
                    <CurrencySelect value={field.value} onChange={field.onChange} />
                  )}
                />
              </div>
            )}
          </FormField>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-6 pt-6">
          <Controller
            control={form.control}
            name="photos"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-2">
                <MediaPicker
                  scope="PRODUCT_PHOTO"
                  label={t('products.fields.photos')}
                  hint={t('products.fields.photosHint')}
                  accept={ALLOWED_CONTENT_TYPES.image}
                  multiple
                  value={field.value}
                  onChange={field.onChange}
                  links={product?.photos ?? []}
                  disabled={!isEditable}
                />
                {fieldState.error ? (
                  <p className="text-xs text-destructive" role="alert">
                    {fieldState.error.message}
                  </p>
                ) : null}
              </div>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4 pt-6">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={isEco}
              disabled={!isEditable}
              onChange={(e) => form.setValue('eco', e.target.checked, { shouldDirty: true })}
            />
            {t('products.fields.ecoProduct')}
          </label>

          {isEco ? (
            <Controller
              control={form.control}
              name="ecoCertificates"
              render={({ field, fieldState }) => {
                const profileEcoCerts = profile?.ecoCertificates ?? [];
                const profileEcoIds = profileEcoCerts.map((l) => l.id);
                const currentIds = field.value ?? [];
                const missingFromProfile = profileEcoIds.filter(
                  (id) => !currentIds.includes(id),
                );
                const combinedLinks = [
                  ...(product?.ecoCertificates ?? []),
                  ...profileEcoCerts.filter(
                    (l) =>
                      !(product?.ecoCertificates ?? []).some((p) => p.id === l.id),
                  ),
                ];
                const handleCopyFromProfile = () => {
                  if (!missingFromProfile.length) return;
                  field.onChange([...currentIds, ...missingFromProfile]);
                };
                const canCopy = isEditable && missingFromProfile.length > 0;
                return (
                  <div className="flex flex-col gap-2">
                    {profileEcoIds.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={handleCopyFromProfile}
                          disabled={!canCopy}
                          style={{ alignSelf: 'flex-start' }}
                        >
                          {t('products.fields.useProfileCerts')}
                        </Button>
                        <span style={{ fontSize: 12, opacity: 0.65 }}>
                          {t('products.fields.useProfileCertsHint')}
                        </span>
                      </div>
                    ) : null}
                    <MediaPicker
                      scope="ECO_CERTIFICATE"
                      label={t('products.fields.ecoCertificates')}
                      accept={[
                        ...ALLOWED_CONTENT_TYPES.image,
                        ...ALLOWED_CONTENT_TYPES.document,
                      ]}
                      multiple
                      value={field.value}
                      onChange={field.onChange}
                      links={combinedLinks}
                      disabled={!isEditable}
                    />
                    {fieldState.error ? (
                      <p className="text-xs text-destructive" role="alert">
                        {fieldState.error.message}
                      </p>
                    ) : null}
                  </div>
                );
              }}
            />
          ) : null}

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={isStock}
              disabled={!isEditable}
              onChange={(e) => form.setValue('stock', e.target.checked, { shouldDirty: true })}
            />
            {t('products.fields.stockItem')}
          </label>

          {isStock ? (
            <FormField
              label={t('products.fields.stockDiscount')}
              required
              error={form.formState.errors.stockDiscountPct?.message}
              className="max-w-xs"
            >
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min={1}
                  max={90}
                  {...form.register('stockDiscountPct', {
                    setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                  })}
                  disabled={!isEditable}
                />
              )}
            </FormField>
          ) : null}
        </CardContent>
      </Card>

      {submitError ? (
        <Alert type="error" showIcon message={submitError} />
      ) : null}

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--ant-color-bg-elevated)',
          border: '1px solid var(--ant-color-border-secondary)',
          borderRadius: 12,
          padding: 12,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
          zIndex: 10,
        }}
      >
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.products)}
          >
            {t('common.back')}
          </Button>
          {product && canArchiveProduct(product.status) ? (
            <Button
              type="button"
              variant="destructive"
              onClick={handleArchive}
              loading={archiveMutation.isPending}
            >
              {t('common.archive')}
            </Button>
          ) : null}
        </div>

        <div className="flex gap-2">
          {product && canSubmitProduct(product.status) ? (
            <Button
              type="button"
              variant="outline"
              loading={submitMutation.isPending}
              disabled={Boolean(submitDisabledReason) || form.formState.isDirty}
              title={
                submitDisabledReason ??
                (form.formState.isDirty ? t('products.saveDisabled.unsaved') : undefined)
              }
              onClick={handleSubmitForModeration}
            >
              {t('products.submit')}
            </Button>
          ) : null}
          <Button
            type="submit"
            loading={createMutation.isPending || updateMutation.isPending}
            disabled={!isEditable || (!form.formState.isDirty && Boolean(product))}
          >
            {product ? t('common.save') : t('common.create')}
          </Button>
        </div>
      </div>
    </form>
      </FormProvider>
  );
}
